import React from "react";
import PropTypes from "prop-types";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

const COUNTRIES_GEO_URL = "/world-110m.json";

// Mapping of region names to arrays of country names (as in GeoJSON)
const REGION_COUNTRIES = {
  "North America": [
    "Canada",
    "United States of America",
    "Mexico",
    "Greenland",
    "Bermuda",
    "Saint Pierre and Miquelon",
    "Bahamas",
    "Cuba",
    "Jamaica",
    "Haiti",
    "Dominican Republic",
    "Barbados",
    "Saint Lucia",
    "Saint Vincent and the Grenadines",
    "Grenada",
    "Trinidad and Tobago",
    "Antigua and Barbuda",
    "Saint Kitts and Nevis",
    "Belize",
    "Guatemala",
    "Honduras",
    "El Salvador",
    "Nicaragua",
    "Costa Rica",
    "Panama",
  ],
  "South America": [
    "Argentina",
    "Bolivia",
    "Brazil",
    "Chile",
    "Colombia",
    "Ecuador",
    "Guyana",
    "Paraguay",
    "Peru",
    "Suriname",
    "Uruguay",
    "Venezuela",
    "French Guiana",
  ],
  Europe: [
    "Albania",
    "Andorra",
    "Austria",
    "Belarus",
    "Belgium",
    "Bosnia and Herzegovina",
    "Bulgaria",
    "Croatia",
    "Czech Republic",
    "Denmark",
    "Estonia",
    "England",
    "Finland",
    "France",
    "Germany",
    "Greece",
    "Hungary",
    "Iceland",
    "Ireland",
    "Italy",
    "Kosovo",
    "Latvia",
    "Liechtenstein",
    "Lithuania",
    "Luxembourg",
    "Malta",
    "Moldova",
    "Monaco",
    "Montenegro",
    "Netherlands",
    "North Macedonia",
    "Norway",
    "Poland",
    "Portugal",
    "Romania",
    "San Marino",
    "Serbia",
    "Scotland",
    "Slovakia",
    "Slovenia",
    "Spain",
    "Sweden",
    "Switzerland",
    "Ukraine",
    "United Kingdom",
    "Vatican",
    "Wales",
  ],
  Africa: [
    "Algeria",
    "Angola",
    "Benin",
    "Botswana",
    "Burkina Faso",
    "Burundi",
    "Cabo Verde",
    "Cameroon",
    "Central African Republic",
    "Chad",
    "Comoros",
    "Congo",
    "Democratic Republic of the Congo",
    "Djibouti",
    "Egypt",
    "Equatorial Guinea",
    "Eritrea",
    "Eswatini",
    "Ethiopia",
    "Gabon",
    "Gambia",
    "Ghana",
    "Guinea",
    "Guinea-Bissau",
    "Ivory Coast",
    "Kenya",
    "Lesotho",
    "Liberia",
    "Libya",
    "Madagascar",
    "Malawi",
    "Mali",
    "Mauritania",
    "Mauritius",
    "Morocco",
    "Mozambique",
    "Namibia",
    "Niger",
    "Nigeria",
    "Rwanda",
    "Sao Tome and Principe",
    "Senegal",
    "Seychelles",
    "Sierra Leone",
    "Somalia",
    "South Africa",
    "South Sudan",
    "Sudan",
    "Tanzania",
    "Togo",
    "Tunisia",
    "Uganda",
    "Zambia",
    "Zimbabwe",
    "Western Sahara",
  ],
  Asia: [
    "Afghanistan",
    "Armenia",
    "Azerbaijan",
    "Bahrain",
    "Bangladesh",
    "Bhutan",
    "Brunei",
    "Cambodia",
    "China",
    "Cyprus",
    "East Timor",
    "Georgia",
    "India",
    "Indonesia",
    "Iran",
    "Iraq",
    "Israel",
    "Japan",
    "Jordan",
    "Kazakhstan",
    "Kuwait",
    "Kyrgyzstan",
    "Laos",
    "Lebanon",
    "Malaysia",
    "Maldives",
    "Mongolia",
    "Myanmar",
    "Nepal",
    "North Korea",
    "Oman",
    "Pakistan",
    "Palestine",
    "Philippines",
    "Qatar",
    "Saudi Arabia",
    "Singapore",
    "South Korea",
    "Sri Lanka",
    "Syria",
    "Taiwan",
    "Tajikistan",
    "Thailand",
    "Turkey",
    "Turkmenistan",
    "United Arab Emirates",
    "Uzbekistan",
    "Vietnam",
    "Yemen",
  ],
  Oceania: [
    "Australia",
    "Fiji",
    "Kiribati",
    "Marshall Islands",
    "Micronesia",
    "Nauru",
    "New Zealand",
    "Palau",
    "Papua New Guinea",
    "Samoa",
    "Solomon Islands",
    "Tonga",
    "Tuvalu",
    "Vanuatu",
  ],
};

const cardStyle = (brandColor) => ({
  background: `linear-gradient(135deg, rgba(124,58,237,0.18) 0%, rgba(255,255,255,0.10) 100%)`,
  border: `2.5px solid ${brandColor || "#7C3AED"}33`,
  boxShadow: `0 8px 32px 0 rgba(124,58,237,0.15)`,
  borderRadius: "2.5rem",
  padding: "3.5rem 2rem 2.5rem 2rem",
  margin: "2.5rem 0",
  color: brandColor || "#7C3AED",
  fontWeight: 700,
  fontSize: "2rem",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "600px",
  minHeight: "520px",
  letterSpacing: "0.01em",
  transition: "box-shadow 0.2s",
  maxWidth: "100%",
});

export default function MarketsMapCard({ regions, palette }) {
  if (!regions || regions.length === 0) {
    return (
      <div className="text-white/60 text-center py-4">No markets selected</div>
    );
  }

  // Expand region names to country names if needed
  const expandedCountries = regions.flatMap(
    (regionOrCountry) => REGION_COUNTRIES[regionOrCountry] || [regionOrCountry]
  );

  // Create a comprehensive mapping of all possible name variations
  const countryNameVariations = {
    "united states of america": ["United States", "USA", "US", "United States of America"],
    "united states": ["United States of America", "USA", "US", "United States"],
    "canada": ["Canada"],
    "australia": ["Australia"],
    "united kingdom": ["United Kingdom", "UK", "Great Britain", "England", "Scotland", "Wales"],
    "uk": ["United Kingdom", "Great Britain", "England", "Scotland", "Wales"],
  };

  // Create a normalized set for case-insensitive matching
  const normalizedExpandedCountries = new Set(
    expandedCountries.map((c) => c.toLowerCase().trim())
  );

  // Also add all variations to the set
  expandedCountries.forEach((country) => {
    const normalized = country.toLowerCase().trim();
    if (countryNameVariations[normalized]) {
      countryNameVariations[normalized].forEach((variation) => {
        normalizedExpandedCountries.add(variation.toLowerCase().trim());
      });
    }
  });

  // Helper function to check if a country should be highlighted
  const isCountryHighlighted = (countryName) => {
    if (!countryName) return false;
    
    const normalizedName = countryName.toLowerCase().trim();
    
    // Direct match (case-insensitive)
    if (normalizedExpandedCountries.has(normalizedName)) {
      return true;
    }
    
    // Check if the GeoJSON name matches any expanded country (with variations)
    for (const expandedCountry of expandedCountries) {
      const normalizedExpanded = expandedCountry.toLowerCase().trim();
      
      // Direct match
      if (normalizedName === normalizedExpanded) {
        return true;
      }
      
      // Check variations for this expanded country
      if (countryNameVariations[normalizedExpanded]) {
        const variations = countryNameVariations[normalizedExpanded];
        if (variations.some(v => normalizedName === v.toLowerCase().trim())) {
          return true;
        }
      }
      
      // Substring matching (e.g., "United States" matches "United States of America")
      // Remove common suffixes for better matching
      const nameWithoutSuffix = normalizedName.replace(/\s+of\s+america$/, "").trim();
      const expandedWithoutSuffix = normalizedExpanded.replace(/\s+of\s+america$/, "").trim();
      
      if (nameWithoutSuffix === expandedWithoutSuffix ||
          nameWithoutSuffix.includes(expandedWithoutSuffix) || 
          expandedWithoutSuffix.includes(nameWithoutSuffix)) {
        return true;
      }
      
      // Also check original names with includes
      if (normalizedName.includes(normalizedExpanded) || 
          normalizedExpanded.includes(normalizedName)) {
        return true;
      }
    }
    
    // Special handling for common name mismatches
    // GeoJSON might have "United States" while we have "United States of America"
    if (normalizedName === "united states" && normalizedExpandedCountries.has("united states of america")) {
      return true;
    }
    if (normalizedName === "united states of america" && normalizedExpandedCountries.has("united states")) {
      return true;
    }
    
    return false;
  };

  return (
    <div
      className="w-full flex flex-col items-center justify-center max-w-7xl mx-auto"
      style={{
        background: "transparent",
        boxShadow: "none",
        border: "none",
        padding: 0,
        margin: 0,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          aspectRatio: "2/1",
          margin: "0 auto",
          background: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ComposableMap
          projection="geoEqualEarth"
          width={1200}
          height={720}
          projectionConfig={{ scale: 180, center: [0, 20] }}
          style={{ width: "100%", height: "auto", background: "transparent" }}
        >
          <Geographies geography={COUNTRIES_GEO_URL}>
            {({ geographies }) =>
              geographies
                .filter((geo) => {
                  const name = geo.properties.name;
                  return (
                    name &&
                    name !== "Antarctica" &&
                    name !== "Arctic" &&
                    name !== "Arctic Ocean"
                  );
                })
                .map((geo) => {
                  const country = geo.properties.name;
                  const isHighlighted = isCountryHighlighted(country);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      style={{
                        default: {
                          fill: isHighlighted
                            ? palette?.primary || "#7C3AED"
                            : "transparent",
                          stroke: palette?.primary || "#7C3AED",
                          strokeWidth: 1.2,
                          outline: "none",
                        },
                        hover: {
                          fill: isHighlighted
                            ? palette?.primary || "#7C3AED"
                            : "rgba(124,58,237,0.15)",
                          stroke: palette?.primary || "#7C3AED",
                          strokeWidth: 1.2,
                          outline: "none",
                          filter: "brightness(1.1)",
                        },
                        pressed: {
                          fill: isHighlighted
                            ? palette?.primary || "#7C3AED"
                            : "transparent",
                          stroke: palette?.primary || "#7C3AED",
                          strokeWidth: 1.2,
                          outline: "none",
                        },
                      }}
                    />
                  );
                })
            }
          </Geographies>
        </ComposableMap>
      </div>
      <div className="flex flex-wrap justify-center gap-4 mt-8">
        {regions.map((region) => (
          <span
            key={region}
            style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: "1.1rem",
              background: "rgba(255,255,255,0.08)",
              borderRadius: "1rem",
              padding: "0.4rem 1.1rem",
              margin: "0.2rem 0.4rem",
              whiteSpace: "nowrap",
              maxWidth: "90%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
            }}
          >
            {region}
          </span>
        ))}
      </div>
    </div>
  );
}

MarketsMapCard.propTypes = {
  regions: PropTypes.arrayOf(PropTypes.string),
  palette: PropTypes.object,
};
